import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Users, Contact, Briefcase, FileText, FileSignature, LifeBuoy, CalendarClock, Zap, User, NotebookPen } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const GROUP_ICON = {
  clients: Users, crm_contacts: Contact, crm_opportunities: Briefcase,
  proposals: FileText, contracts: FileSignature, tickets: LifeBuoy,
  calendar: CalendarClock, automations: Zap, team: User, notes: NotebookPen,
};

export function GlobalSearchDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setQ(""); setGroups([]); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (q.trim().length < 2) { setGroups([]); setLoading(false); return undefined; }
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setGroups(data.groups || []);
      } catch { setGroups([]); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q, open]);

  const go = (route) => { onOpenChange(false); navigate(route); };
  const total = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl overflow-hidden p-0" data-testid="global-search-dialog">
        <VisuallyHidden>
          <DialogTitle>Ricerca globale</DialogTitle>
          <DialogDescription>Cerca in tutta la piattaforma</DialogDescription>
        </VisuallyHidden>
        <div className="flex items-center gap-2 border-b border-border/60 px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca clienti, lead, preventivi, contratti, ticket, appuntamenti, automazioni…"
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            data-testid="global-search-input"
          />
          {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
        </div>
        <div className="max-h-[420px] overflow-y-auto p-2" data-testid="global-search-results">
          {q.trim().length < 2 && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              Digita almeno 2 caratteri: la ricerca copre tutti i moduli della piattaforma.
            </p>
          )}
          {q.trim().length >= 2 && !loading && total === 0 && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground" data-testid="global-search-empty">Nessun risultato per “{q}”.</p>
          )}
          {groups.map((g) => {
            const Icon = GROUP_ICON[g.key] || Search;
            return (
              <div key={g.key} className="mb-1">
                <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</div>
                {g.items.map((it) => (
                  <button
                    key={`${g.key}-${it.id}`}
                    onClick={() => go(it.route)}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm hover:bg-muted"
                    data-testid={`global-search-item-${g.key}`}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-foreground">{it.title}</span>
                      {it.subtitle && <span className="block truncate text-xs text-muted-foreground">{it.subtitle}</span>}
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
